/**
 * Contact Card Component - Minimal Version
 */

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Building2, Calendar, Check } from "lucide-react";
import { Link } from "react-router-dom";
import type { Contact } from "@/lib/crm/types";
import { getContactUrgency } from "@/lib/crm/urgency";
import { formatUiDate } from "@/lib/ui-format";
import { cn } from "@/lib/utils";
import { useMarkDone } from "@/hooks/crm/useContacts";

interface ContactCardProps {
  contact: Contact;
  isOverdue?: boolean;
}

export function ContactCard({ contact, isOverdue = false }: ContactCardProps): JSX.Element {
  const urgency = getContactUrgency(contact);
  const { indicator } = urgency;
  const markDone = useMarkDone();
  const contactNameId = `crm-contact-name-${contact.id}`;
  const openContactLabelId = `crm-open-contact-label-${contact.id}`;
  const markDoneLabelId = `crm-mark-done-label-${contact.id}`;

  const handleMarkDone = (): void => {
    markDone.mutate(contact.id);
  };

  return (
    <Card
      className={cn(
        "interactive h-full p-4 transition-shadow",
        "border-border hover:border-border-strong hover:shadow-elevation-2",
        isOverdue && "border-status-danger/40"
      )}
    >
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-2">
          {/* Only the header block is a link — a nested button elsewhere in
              the card (Mark done, below) would otherwise be invalid HTML
              and an unreachable a11y target if the whole card were one. */}
          <Link
            to={`/crm/contact/${encodeURIComponent(contact.id)}`}
            className="min-w-0 flex-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-labelledby={`${openContactLabelId} ${contactNameId}`}
          >
            <span id={openContactLabelId} className="sr-only">
              Open contact
            </span>
            <h3
              id={contactNameId}
              className="font-semibold text-foreground"
              data-content-origin="external"
            >
              {contact.name}
            </h3>
            {contact.company && (
              <div className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                <Building2 className="h-3 w-3" aria-hidden="true" />
                <span data-content-origin="external">{contact.company}</span>
              </div>
            )}
          </Link>

          <span
            className={cn(
              "mt-1 h-2 w-2 shrink-0 rounded-full",
              indicator.className,
              indicator.animated && "animate-pulse"
            )}
            aria-hidden="true"
          />

          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-11 w-11 shrink-0"
            disabled={markDone.isPending}
            onClick={handleMarkDone}
            aria-labelledby={`${markDoneLabelId} ${contactNameId}`}
          >
            <Check className="h-4 w-4" aria-hidden="true" />
            <span id={markDoneLabelId} className="sr-only">
              Mark done
            </span>
          </Button>
        </div>

        {contact.pain_point && (
          <p
            className="line-clamp-2 text-sm text-text-secondary"
            data-content-origin="domain"
          >
            {contact.pain_point}
          </p>
        )}

        {contact.next_action && (
          <div className="flex items-start gap-2">
            <Badge variant="outline" className="border-primary/30 bg-primary/10 text-primary text-xs">
              Next
            </Badge>
            <p
              className="flex-1 text-xs text-muted-foreground"
              data-content-origin="domain"
            >
              {contact.next_action}
            </p>
          </div>
        )}

        {contact.due_date && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Calendar className="h-3 w-3" aria-hidden="true" />
            <span>{formatUiDate(contact.due_date)}</span>
          </div>
        )}
      </div>
    </Card>
  );
}
